import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  signal,
} from "@angular/core";
import { PadlockService } from "../../padlock/padlock.service";
import { Workspace } from "../state/workspaces.model";

@Component({
  selector: "app-workspace-avatar",
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    "[class.recent]": 'size() === "recent"',
    "[class.card]": 'size() === "card"',
  },
  template: `
    @if (logoUrl(); as url) {
      <img [src]="url" alt="" />
    } @else {
      <span [class]="fallbackClass()">{{ initials() }}</span>
    }
  `,
  styles: `
    :host {
      display: block;
      flex: 0 0 auto;
    }

    :host.recent {
      width: 34px;
      height: 34px;
    }

    :host.card {
      width: 48px;
      height: 48px;
    }

    img,
    span {
      display: flex;
      width: 100%;
      height: 100%;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
    }

    img {
      object-fit: contain;
      background: #ffffff;
      padding: 1px;
    }

    :host.recent span {
      font-size: 1.2rem;
      font-weight: 600;
    }

    :host.card span {
      font-size: 1.6rem;
      font-weight: 600;
    }

    .avatar-ultramarine {
      background: var(--fds-color-ultramarine-100);
      color: var(--fds-color-ultramarine-800);
    }

    .avatar-jade {
      background: var(--fds-color-jade-100);
      color: var(--fds-color-jade-900);
    }

    .avatar-tangerine {
      background: var(--fds-color-tangerine-100);
      color: var(--fds-color-tangerine-800);
    }

    .avatar-violet {
      background: var(--fds-color-violet-100);
      color: var(--fds-color-violet-800);
    }

    .avatar-green {
      background: var(--fds-color-green-100);
      color: var(--fds-color-green-900);
    }

    .avatar-pink {
      background: var(--fds-color-pink-100);
      color: var(--fds-color-pink-800);
    }

    .avatar-infoBlue {
      background: var(--fds-color-infoBlue-100);
      color: var(--fds-color-infoBlue-900);
    }

    .avatar-seafoam {
      background: var(--fds-color-seafoam-100);
      color: var(--fds-color-seafoam-900);
    }
  `,
})
export class WorkspaceAvatarComponent {
  private padlock = inject(PadlockService);

  workspace = input.required<Workspace>();
  initials = input.required<string>();
  fallbackClass = input.required<string>();
  size = input<"recent" | "card">("card");
  logoUrl = signal<string | null>(null);

  constructor() {
    effect((onCleanup) => {
      const workspace = this.workspace();
      this.logoUrl.set(null);
      if (!workspace.has_logo || !workspace.logo_version) return;

      let objectUrl: string | null = null;
      const subscription = this.padlock
        .getTenantLogo(workspace.uuid, workspace.logo_version)
        .subscribe({
          next: (blob) => {
            objectUrl = URL.createObjectURL(blob);
            this.logoUrl.set(objectUrl);
          },
          error: () => this.logoUrl.set(null),
        });

      onCleanup(() => {
        subscription.unsubscribe();
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      });
    });
  }
}